import React, { FunctionComponent, useEffect, useState } from "react";
import styled from "styled-components";
import { Select } from "antd";
import { IGPSAMChartData } from "../../../Models/Models";
import { DropdownComparisonWeight } from "../../../Enums/Enums";
import * as am4charts from "@amcharts/amcharts4/charts";
import * as am4core from "@amcharts/amcharts4/core";
import am4themes_animated from "@amcharts/amcharts4/themes/animated";
import { ChartButtonWrapper, ChartBodyWrapper } from "../";
import { IRetailMatchChartData } from "../../../Models/IRetailMatch";

const { Option } = Select;

interface IAMXYChart {
  data: IGPSAMChartData;
  retailMatchData: IRetailMatchChartData[];
  chartId: string;
  xLabel?: string;
  yLabel?: string;
  rotateXLabels?: boolean;
}

const ChartContainer = styled("div")`
  min-height: 360px;
`;

export const AMXYChart: FunctionComponent<IAMXYChart> = (props: IAMXYChart) => {
    useEffect(() => {
        if (props.data && props.data.week.length > 0) {
            setChart(createChart(props.chartId, props.data));
        }
    }, []);

    const [isVisits, setIsVisits] = useState(true);
    const [chart, setChart] = useState(null);
    const [series, setSeries] = useState(null);

    const handleVisitsChange = (value: DropdownComparisonWeight) => {
        switch (value) {
            case DropdownComparisonWeight.PRIMARY:
                setIsVisits(true);
                series.dataFields.valueY = "visitWeight";
                break;
            case DropdownComparisonWeight.SECONDARY:
                setIsVisits(false);
                series.dataFields.valueY = "visitorCount";
                break;
        }
        setSeries(series);
        chart.validateData();
    };

    const createChart = (
        targetDiv: string,
        data: IGPSAMChartData
    ): am4charts.XYChart => {
    //Create the chart object
        am4core.useTheme(am4themes_animated);
        const newChart = am4core.create(targetDiv, am4charts.XYChart);
        newChart.width = am4core.percent(100);
        newChart.align = "center";
        newChart.data = data.week;
        newChart.legend = new am4charts.Legend();

        //Create the X axis
        const xAxis = newChart.xAxes.push(new am4charts.DateAxis());
        xAxis.title.text = props.xLabel;
        // xAxis.renderer.minHeight = 110;
        // xAxis.renderer.

        if (props.rotateXLabels) {
            xAxis.renderer.labels.template.rotation = 270;
            xAxis.renderer.labels.template.horizontalCenter = "right";
            xAxis.renderer.labels.template.verticalCenter = "middle";
            xAxis.renderer.minGridDistance = 10;
            xAxis.renderer.labels.template.fontSize = 10;
        }

        xAxis.renderer.grid.template.location = 0.5;
        xAxis.renderer.labels.template.location = 0.5;

        //Create the Y axis
        const yAxis = newChart.yAxes.push(new am4charts.ValueAxis());
        yAxis.title.text = props.yLabel;
        yAxis.renderer.minWidth = 50;
        yAxis.renderer.labels.template.adapter.add("text", (text, label) => {
            return (label.dataItem as any).value * 100 + "%"; // ERY: Hooray! another am charts typescript bug!
        });
        yAxis.adapter.add("getTooltipText", (text) => {
            return Number(text) * 100 + "%";
        });
        //This will set the Y-axis number format to "10 M" for 10 million, etc.
        // yAxis.numberFormatter.numberFormat = "#%";

        //Bind the properties of my sales data to the series property of the chart
        const newSeries = newChart.series.push(new am4charts.ColumnSeries());
        newSeries.sequencedInterpolation = true;
        newSeries.name = "Selection One";
        if (isVisits) {
            newSeries.dataFields.valueY = "visitWeight"; // TODO ERY: will need to revisit this when we get benchmark data back.
        } else {
            newSeries.dataFields.valueY = "visitorCount";
        }
        newSeries.dataFields.dateX = "id";

        //Set the column colors
        const primaryColor = am4core.color("#5B52F9");
        newSeries.stroke = primaryColor;
        newSeries.columns.template.fill = primaryColor;

        // ERY: no benchmark for now.
        // if (newChart.data.length > 0 && newChart.data[0].benchmark) {
        //   const series2 = newChart.newSeries.push(new am4charts.ColumnSeries());
        //   series2.dataFields.valueY = "benchmark";
        //   series2.dataFields.categoryX = "label";

        //   //Set the column colors
        //   const primaryColor = am4core.color("#02C48C");
        //   series2.stroke = primaryColor;
        //   series2.columns.template.fill = primaryColor;
        // }

        //Set the mouseover text. This will show the original value rather than the abbreviated "10 M"
        newSeries.columns.template.tooltipText = "{name} - {dateX}: {valueY}"; // TODO ERY: Todd's going to probably change this a lot, so I don't want to mess with this too much.

        //const tooltipPosition = scout.isMobile || scout.isMobileDevice ? "fixed" : "pointer";
        /*
    ^^ this was to ensure correct positioning of tooltips when clicking on columns on mobile devices
    It works on everything except the touchscreen tv, so until we figure something else out, we're keeping it on fixed on all devices
    */
        const tooltipPosition = "fixed";
        newSeries.columns.template.tooltipPosition = tooltipPosition;

        //Create X-axis cursor
        newChart.cursor = new am4charts.XYCursor();

        //X Scrollbar, so user can narrow down date ranges
        newChart.scrollbarX = new am4core.Scrollbar();

        // TODO ERY: May Revisit
        //Enable exporting
        // const exportMenu = newChart.exporting.menu = new am4core.ExportMenu();
        // exportMenu.items = [{
        //     label: "Export",
        //     menu: [
        //         { type: "csv", label: "CSV" },
        //         { type: "xlsx", label: "XLSX" }
        //     ]
        // }];
        // exportMenu.align = "right";
        // exportMenu.verticalAlign = "top";

        // newChart.exporting.filePrefix = "Performance";
        setSeries(newSeries);

        return newChart;
    };

    return (
        <ChartBodyWrapper>
            <ChartButtonWrapper>
                <Select
                    defaultValue={DropdownComparisonWeight.PRIMARY}
                    onChange={handleVisitsChange}
                >
                    <Option value={DropdownComparisonWeight.PRIMARY}>Visits</Option>
                    <Option value={DropdownComparisonWeight.SECONDARY}>Visitors</Option>
                </Select>
            </ChartButtonWrapper>
            <ChartContainer id={props.chartId} />
        </ChartBodyWrapper>
    );
};
